import { ref } from "vue";

export type SnackbarType = "success" | "error" | "warning" | "info";

export interface SnackbarMessage {
  id: number;
  title?: string;
  message: string;
  type: SnackbarType;
  duration?: number;
}

// Mantenemos la variable de estado fuera del export de la función
// para que sea verdaderamente reactiva y global en toda la aplicación
const snackbars = ref<SnackbarMessage[]>([]);
let nextId = 0;

export function useSnackbar() {
  const showSnackbar = (options: Omit<SnackbarMessage, "id">) => {
    // Regla especial para Sesión Expirada: es prioritaria y excluyente
    if (options.title === "Sesión Expirada") {
      snackbars.value = []; // Limpiamos cualquier otra alerta existente
    } else {
      // Si ya se está mostrando Sesión Expirada, bloqueamos cualquier otra alerta nueva
      const hasSessionExpired = snackbars.value.some(
        (s) => s.title === "Sesión Expirada"
      );
      if (hasSessionExpired) return;
    }

    // Evitar mostrar la misma alerta múltiple veces si ya está activa
    const isDuplicate = snackbars.value.some(
      (s) =>
        s.title === options.title &&
        s.message === options.message &&
        s.type === options.type
    );
    if (isDuplicate) return;

    const id = nextId++;
    const snackbar: SnackbarMessage = {
      id,
      ...options,
      duration: options.duration || 6000, // 6 segundos por defecto
    };

    snackbars.value.push(snackbar);

    // Auto-ocultado
    if (snackbar.duration && snackbar.duration > 0) {
      setTimeout(() => {
        removeSnackbar(id);
      }, snackbar.duration);
    }
  };

  const removeSnackbar = (id: number) => {
    snackbars.value = snackbars.value.filter((s) => s.id !== id);
  };

  return {
    snackbars,
    showSnackbar,
    removeSnackbar,
  };
}
